import axios from 'axios';
import type { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ApiError } from '../types';
import type { ApiConfig, ApiResponse } from '../types';
import { Capacitor } from '@capacitor/core';
import { displayLocale } from '../i18n/locale';
import {
  ORG_GUARD_HEADER,
  ORG_HEADER,
  leaveForActiveOrg,
  noteServerAcceptsOrgHeader,
  orgHeaderFor,
} from '../lib/orgGuard';

// ---------------------------------------------------------------------------
// Platform detection — cached once at startup
// ---------------------------------------------------------------------------
const isNative = Capacitor.isNativePlatform();

// ---------------------------------------------------------------------------
// Access token — in-memory on web (never persisted, XSS-safe).
// On native (Capacitor), it is ALSO persisted alongside its expiry so a cold
// app launch can re-use it without a mandatory /auth/refresh round-trip.
// This is safe on native because the WebView loads only the bundled app code;
// it is NOT safe on web where scripts from elsewhere could read localStorage.
// ---------------------------------------------------------------------------
let _accessToken: string | null = null;
let _accessTokenExpiresAt: number | null = null; // epoch ms
const ACCESS_TOKEN_KEY = '_at';
const ACCESS_TOKEN_EXPIRY_KEY = '_ate';
// Treat tokens within this window of expiry as stale — forces a refresh a bit
// early so in-flight requests don't race the expiration.
const ACCESS_TOKEN_SKEW_MS = 60_000;

// ---------------------------------------------------------------------------
// Refresh token storage — only used on native platforms (iOS/Android).
// On web, the refresh token is stored in an httpOnly cookie by the server.
// On native, iOS WKWebView blocks cross-origin SameSite=None cookies, so
// the server returns the refresh token in the response body and we store
// it here and send it via X-Refresh-Token header.
// ---------------------------------------------------------------------------
const REFRESH_TOKEN_KEY = '_rt';

export function getStoredRefreshToken(): string | null {
  if (!isNative) return null;
  try { return localStorage.getItem(REFRESH_TOKEN_KEY); } catch { return null; }
}

function storeRefreshToken(token: string): void {
  if (!isNative) return;
  try { localStorage.setItem(REFRESH_TOKEN_KEY, token); } catch { /* private mode */ }
}

function clearRefreshToken(): void {
  if (!isNative) return;
  try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch { /* private mode */ }
}

// ---------------------------------------------------------------------------
// Refresh-failure cooldown — prevents infinite 401 → redirect → reload loops.
// Uses sessionStorage so the flag survives a full page reload.
// ---------------------------------------------------------------------------
const REFRESH_FAILURE_COOLDOWN_MS = 15_000;

export function markRefreshFailed(): void {
  try { sessionStorage.setItem('_refreshFailedAt', String(Date.now())); } catch { /* private mode */ }
}

export function isRefreshRecentlyFailed(): boolean {
  try {
    const stored = sessionStorage.getItem('_refreshFailedAt');
    if (stored && Date.now() - Number(stored) < REFRESH_FAILURE_COOLDOWN_MS) return true;
  } catch { /* private mode */ }
  return false;
}

export function clearRefreshFailure(): void {
  try { sessionStorage.removeItem('_refreshFailedAt'); } catch { /* private mode */ }
}

/**
 * Redirect to /login on a 401. No-op if we are already on /login — otherwise
 * we self-loop the page (every API call from /login itself triggers another
 * full-page replace, preventing the user from logging back in).
 */
function redirectToLogin(): void {
  try {
    if (typeof window !== 'undefined' && window.location.pathname === '/login') return;
  } catch { /* ignore */ }
  window.location.replace('/login');
}

export function getToken(): string | null {
  return _accessToken;
}

export function setAccessToken(token: string, expiresAt?: string | number | null): void {
  _accessToken = token;
  _accessTokenExpiresAt = expiresAt ? new Date(expiresAt).getTime() : null;
  // Persist on native only — see ACCESS_TOKEN_KEY comment above.
  if (isNative) {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
      if (_accessTokenExpiresAt) {
        localStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, String(_accessTokenExpiresAt));
      } else {
        localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
      }
    } catch { /* private mode */ }
  }
}

export function clearAccessToken(): void {
  _accessToken = null;
  _accessTokenExpiresAt = null;
  clearRefreshToken();
  if (isNative) {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
    } catch { /* private mode */ }
  }
  // Belt-and-suspenders: remove any stale values left by older app versions
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
  localStorage.removeItem('remember_me');
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('user');
}

/**
 * On native cold start, rehydrate a persisted access token if it still has
 * enough lifetime to be useful. Returns true if a token was hydrated.
 * Callers (AuthContext bootstrap) can skip the /auth/refresh round-trip when
 * this returns true, dropping 1 network hop off app launch.
 */
export function hydrateAccessTokenIfFresh(): boolean {
  if (!isNative) return false;
  try {
    const stored = localStorage.getItem(ACCESS_TOKEN_KEY);
    const expiryRaw = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    if (!stored || !expiryRaw) return false;
    const expiry = Number(expiryRaw);
    if (!Number.isFinite(expiry)) return false;
    if (Date.now() + ACCESS_TOKEN_SKEW_MS >= expiry) {
      // Near or past expiry — drop it so the normal refresh path runs
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
      return false;
    }
    _accessToken = stored;
    _accessTokenExpiresAt = expiry;
    return true;
  } catch {
    return false;
  }
}

/**
 * Handle refresh token from server response — on native platforms, the server
 * returns it in the body instead of setting a cookie.
 */
export function handleRefreshTokenFromResponse(data: any): void {
  if (isNative && data?.refreshToken) {
    storeRefreshToken(data.refreshToken);
  }
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  // Tracks an in-flight refresh so concurrent 401s share one refresh request
  private refreshPromise: Promise<string> | null = null;

  constructor(config: ApiConfig) {
    this.baseURL = config.baseURL;
    this.api = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: {
        'Content-Type': 'application/json',
        // Tell the server this is a native Capacitor client so it returns
        // refresh tokens in the response body instead of cookies
        ...(isNative && { 'X-Client-Type': 'capacitor' }),
      },
      withCredentials: true // Enable sending cookies with requests (web only)
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor — attach in-memory access token
    this.api.interceptors.request.use(
      (config) => {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Name the organisation this tab is showing, so the server can refuse
        // the request if the account has since moved — see lib/orgGuard.ts.
        // The reader's language, so server sentences can follow it once they
        // are translated. A CORS-safelisted header: no preflight change.
        config.headers['Accept-Language'] = displayLocale();
        const orgId = orgHeaderFor(config.url);
        if (orgId) {
          config.headers[ORG_HEADER] = orgId;
        }
        // The instance default is application/json, and with that header axios
        // serialises a FormData body to JSON — the file becomes `{}` and the
        // server sees no upload. Drop it so the browser sets multipart with its
        // boundary.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          config.headers.delete('Content-Type');
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        if (response.headers?.[ORG_GUARD_HEADER]) noteServerAcceptsOrgHeader();
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        if (error.response?.headers?.[ORG_GUARD_HEADER]) noteServerAcceptsOrgHeader();

        let defaultMessage: string;
        if (!error.response) {
          if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            defaultMessage = 'Request timed out. Please try again.';
          } else {
            defaultMessage = 'Cannot connect to the server. Please check your internet connection.';
          }
        } else {
          defaultMessage = 'An unexpected server error occurred. Please try again.';
        }

        const apiError = new ApiError(defaultMessage, error.response?.status || 500);
        // Set before any of the branches below return, so every rejection path
        // carries it.
        apiError.offline = !error.response;

        if (error.response?.data) {
          const errorData = error.response.data as any;
          apiError.message = errorData.error?.message || errorData.message || error.message;
          apiError.errors = errorData.errors;
          if (errorData.error?.code) apiError.code = errorData.error.code;
          // Preserve the full error body so callers can read structured fields
          // (e.g. credential gaps on a 422, T&C version on a 428).
          if (errorData.error && typeof errorData.error === 'object') {
            apiError.details = errorData.error;
          }
        }

        // The account is in another organisation than this tab is showing
        // (switched in another tab or on another device). Nothing was read or
        // written; move the tab rather than leave it on a screen that lies.
        if (error.response?.status === 409 && apiError.code === 'ORG_CHANGED') {
          const activeOrgId = apiError.details?.active_organisation_id;
          leaveForActiveOrg(typeof activeOrgId === 'number' ? activeOrgId : null);
          return Promise.reject(apiError);
        }

        const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');

        // If refresh recently failed, skip straight to login — breaks the
        // reload → initializeAuth → refresh → 401 → reload loop.
        if (error.response?.status === 401 && isRefreshRecentlyFailed()) {
          clearAccessToken();
          redirectToLogin();
          return Promise.reject(apiError);
        }

        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
          originalRequest._retry = true;

          try {
            // Deduplicate: if a refresh is already in flight, wait for it
            if (!this.refreshPromise) {
              const refreshHeaders: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(isNative && { 'X-Client-Type': 'capacitor' }),
              };

              // On native, send the stored refresh token via header
              const storedRefreshToken = getStoredRefreshToken();
              if (isNative && storedRefreshToken) {
                refreshHeaders['X-Refresh-Token'] = storedRefreshToken;
              }

              this.refreshPromise = axios
                .post<ApiResponse<{ accessToken: string; expiresAt?: string; refreshToken?: string }>>(
                  `${this.baseURL}/auth/refresh`,
                  {},
                  {
                    withCredentials: true,
                    headers: refreshHeaders,
                  }
                )
                .then((res) => {
                  if (res.data.success && res.data.data?.accessToken) {
                    // Store new refresh token on native platforms
                    handleRefreshTokenFromResponse(res.data.data);
                    // Persist the new access token + expiry so subsequent cold
                    // starts can skip the /auth/refresh round-trip.
                    setAccessToken(res.data.data.accessToken, res.data.data.expiresAt);
                    return res.data.data.accessToken;
                  }
                  throw new Error('No access token in refresh response');
                })
                .finally(() => {
                  this.refreshPromise = null;
                });
            }

            const newToken = await this.refreshPromise;
            // setAccessToken was already called inside the refresh promise; this
            // assignment is defensive in case the promise resolved via a concurrent path.
            if (getToken() !== newToken) setAccessToken(newToken);

            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.api(originalRequest);
          } catch {
            markRefreshFailed();
            clearAccessToken();
            redirectToLogin();
            return Promise.reject(apiError);
          }
        } else if (error.response?.status === 401 && !isRefreshRequest) {
          markRefreshFailed();
          clearAccessToken();
          redirectToLogin();
        }

        return Promise.reject(apiError);
      }
    );
  }

  async get<T = any>(url: string, config?: any): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: any,
  ): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(
      url,
      data,
      { ...(config || {}), ...(headers ? { headers: { ...(config?.headers || {}), ...headers } } : {}) },
    );
    return response.data;
  }

  async put<T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: any,
  ): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(
      url,
      data,
      { ...(config || {}), ...(headers ? { headers: { ...(config?.headers || {}), ...headers } } : {}) },
    );
    return response.data;
  }

  async patch<T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: any,
  ): Promise<ApiResponse<T>> {
    const response = await this.api.patch<ApiResponse<T>>(
      url,
      data,
      { ...(config || {}), ...(headers ? { headers: { ...(config?.headers || {}), ...headers } } : {}) },
    );
    return response.data;
  }

  async delete<T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: any,
  ): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(
      url,
      {
        ...(config || {}),
        data,
        ...(headers ? { headers: { ...(config?.headers || {}), ...headers } } : {}),
      },
    );
    return response.data;
  }

  updateBaseURL(newBaseURL: string) {
    this.baseURL = newBaseURL;
    this.api.defaults.baseURL = newBaseURL;
  }

  getBaseURL(): string {
    return this.baseURL;
  }
}

const apiService = new ApiService({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
  timeout: 10000,
});

export default apiService;
